import type { LensDataset } from '~/types/leLens';

import type { DatasetChange } from './generateLensCard';

/**
 * Applies to a board's datasets the change that adding a card implied.
 *
 * A dataset belongs to the board, not to the card that prompted the change, so
 * this is deliberately a pure rewrite the caller saves in the same request as
 * the card. Half of it landing would leave a card bound to a column nothing
 * returns.
 */
export function applyDatasetChange(
  datasets: ReadonlyArray<LensDataset>,
  change: DatasetChange | undefined,
): Array<LensDataset> {
  if (!change) {
    return [...datasets];
  }

  return datasets.map((dataset) => {
    if (dataset.id !== change.datasetId) {
      return dataset;
    }

    if (change.kind === 'field') {
      // A field of the same name is replaced rather than duplicated: two fields
      // called the same thing leave a card's measure ambiguous, and the newer
      // answer is the one just asked for.
      const kept = (dataset.fields ?? []).filter((field) => field.name !== change.field.name);
      return { ...dataset, fields: [...kept, change.field] };
    }

    // An extended query used to be re-checked here against the columns a
    // regex could find in its SQL text, in case the rewrite had silently
    // dropped one a field read. That is now the extend-check guard's job,
    // run before the reader is ever offered Apply: it dry-runs the rewrite
    // and refuses one that drops a column, so a rewrite that reaches this
    // point has already kept every column its fields depend on.
    return { ...dataset, sql: change.sql, columns: undefined };
  });
}

/** What to tell the reader a card cost, when it cost more than nothing. */
export function describeDatasetChange(
  change: DatasetChange | undefined,
  datasets: ReadonlyArray<LensDataset>,
): string | null {
  if (!change) {
    return null;
  }
  const name = datasets.find((dataset) => dataset.id === change.datasetId)?.name ?? change.datasetId;
  if (change.kind === 'field') {
    return `Added ${change.field.name} to “${name}” rather than a new query.`;
  }
  return `Extended “${name}” to return what this card needed, rather than a new query.`;
}
